import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function ReplacementModal({ open, onOpenChange, crew, setCrew }) {
  const [scope, setScope] = useState("recap");
  const [memberId, setMemberId] = useState("");
  const [newName, setNewName] = useState("");
  const [opts, setOpts] = useState([]);
  const [showOpts, setShowOpts] = useState(false);
  const timer = useRef(null);

  useEffect(() => { if (open) { setMemberId(""); setNewName(""); setOpts([]); } }, [open]);

  const present = crew.filter((m) => !m.replaced);
  const selMember = crew.find((m) => m.id === memberId);
  const nameOf = (m) => m.nom_prenom?.trim() || `${m.fonction || "—"} (sans nom)`;

  const fetchNames = (text) => {
    setNewName(text);
    if (timer.current) clearTimeout(timer.current);
    if (!text || text.trim().length < 1 || !selMember) { setOpts([]); return; }
    const role = selMember.category === "ouvreur" ? "chef_hotesse" : "crew_boss";
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/team-lead-candidates", { params: { role, q: text } });
        setOpts((data || []).map((d) => [d.nom, d.prenom].filter(Boolean).join(" ")));
        setShowOpts(true);
      } catch { setOpts([]); }
    }, 200);
  };

  const confirm = () => {
    if (!selMember) { toast.error("Choisissez la personne à remplacer"); return; }
    if (!newName.trim()) { toast.error("Indiquez la personne remplaçante"); return; }
    const replacement = {
      ...selMember, id: crypto.randomUUID(), nom_prenom: newName.trim(),
      replaced: false, present: false, signature_matin: "", signature_soir: "",
    };
    setCrew((prev) => prev.map((m) => (m.id === memberId ? { ...m, replaced: true } : m)).concat([replacement]));
    toast.success("Remplacement déclaré");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="replacement-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Déclarer un remplacement</DialogTitle>
          <DialogDescription>La personne remplacée est conservée (marquée « remplacée ») et la remplaçante est ajoutée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Onglet</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="rep-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recap">Récap 48h</SelectItem>
                <SelectItem value="onsite">ON SITE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Personne présente à remplacer</Label>
            <Select value={memberId} onValueChange={(v) => { setMemberId(v); setNewName(""); setOpts([]); }}>
              <SelectTrigger data-testid="rep-member"><SelectValue placeholder="Choisir une personne" /></SelectTrigger>
              <SelectContent>
                {present.map((m) => <SelectItem key={m.id} value={m.id}>{nameOf(m)} — {m.category === "ouvreur" ? "Ouvreur/se" : "Tech"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 relative">
            <Label className="text-xs">Personne remplaçante (base {selMember?.category === "ouvreur" ? "ouvreuses" : "intermittents"})</Label>
            <Input value={newName} disabled={!selMember} autoComplete="off" placeholder="Tapez un nom..." data-testid="rep-newname"
              onChange={(e) => fetchNames(e.target.value)} onFocus={() => newName && setShowOpts(true)}
              onBlur={() => setTimeout(() => setShowOpts(false), 150)} />
            {showOpts && opts.length > 0 && (
              <div className="absolute z-[70] mt-1 w-full max-h-48 overflow-auto rounded-md border border-border bg-popover shadow-xl" data-testid="rep-newname-list">
                {opts.map((o, i) => (
                  <button type="button" key={i} className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
                    onMouseDown={(e) => { e.preventDefault(); setNewName(o); setShowOpts(false); }} data-testid={`rep-newname-option-${i}`}>{o}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={confirm} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="rep-confirm">Valider le remplacement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
